import WorkMonitorView from "pages/workMonitor/WorkMonitorView"
import React, { useContext, useEffect, useMemo, useState } from "react"
import EmployeeInfo from "./EmployeeInfo"
import LocationInfo from "./LocationInfo"
import { CSVLink } from "react-csv"
import { DATE_FORMATS, ShiftStatus } from "config/constants"
import { convertIOSDateToHTMLFormat, sortByProperty } from "config/utils"
import moment from "moment"
import IncidentView from "./IncidentInfo"
import { Card, Divider, Radio } from "antd-v5"
import { useHeightResize } from "hooks"
import { AntdButton } from "components"
import { CloudDownloadOutlined, FilePdfOutlined } from "@ant-design/icons"
import ShiftEventsRetainContext, { ShiftEventsContextProps } from "./ShiftEventsContext"
import { GetScheduleFromTableInterface } from "@type/schedules.types"
import { useAbac } from "react-abac"
import { Permissions } from "config/constants"
import { ReportShiftInterface } from "@type/workMonitor.types"
import logo from "images/logo.png"
import ActivityReportModal from "./ActivityReport/Detail/Main"
import ActivitySummaryModal from "./ActivityReport/Summary/Main"

interface Props {
	data: GetScheduleFromTableInterface
}
const headers = [
	{ label: "Officer Name", key: "employee" },
	{ label: "Site Name", key: "location" },
	{ label: "Date", key: "date" },
	{ label: "Event Type", key: "status" },
	{ label: "Event Time", key: "eventTime" },
	{ label: "QR Name", key: "name" },
	{ label: "Location", key: "latLng" },
	{ label: "Address", key: "" },
	{ label: "Comments", key: "comment" },
]

enum ScheduleInfoTabs {
	"Employee" = "Employee",
	"Site" = "Site",
	"Activity" = "Activity",
}

const ScheduleInfo: React.FC<Props> = ({ data: { _id: scheduleId, employee: employeeId, site } }) => {
	useHeightResize({ element: "schedule-info-body" })
	const { userHasPermissions } = useAbac()
	const { shift, fetchShiftByScheduleId } = useContext(ShiftEventsRetainContext) as ShiftEventsContextProps
	const [refetchShift, setRefetchShift] = useState(false)
	const [activeRadio, setActiveRadio] = useState<string>()
	const [radioItems, setRadioItems] = useState<string[]>([])
	const [openSummaryModal, setOpenSummaryModal] = useState(false)
	const [isShiftActivityViewModalOpen, setIsShiftActivityViewModalOpen] = useState(false)

	useEffect(() => {
		fetchShiftByScheduleId(scheduleId)
	}, [scheduleId, refetchShift])

	const onRefetchShift = () => {
		setRefetchShift(pre => !pre)
	}

	const shiftEventsData = useMemo(() => {
		const data: any[] = []
		if (!shift?.shiftEvents) return []
		shift?.shiftEvents
			.filter(item => !item.isEventMissed)
			.forEach(({ createdAt, geo, eventTime, status, ...shiftEvent }) => {
				if (status === "QR") {
					shiftEvent.scannedQREvents?.forEach(
						({
							createdAt: createdAtQR,
							eventTime: eventTimeQR,
							geo: geoQR,
							status: statusQR,
							qr = null,
							...shiftEvent
						}) => {
							data.push({
								...shiftEvent,
								employee: shift.employee,
								eventTime: convertIOSDateToHTMLFormat(
									eventTimeQR || "",
									DATE_FORMATS.REPORT_DD_MM_YYYY_HH_mm,
								),
								date: convertIOSDateToHTMLFormat(createdAtQR, DATE_FORMATS.REPORT_DD_MM_YYYY),
								latLng: `${geoQR?.coordinates[1].toFixed(4) || ""} , ${
									geoQR?.coordinates[0].toFixed(4) || ""
								}`,
								location: shift?.location,
								status: statusQR,
								name: qr?.name ?? "",
							})
						},
					)
				} else {
					data.push({
						...shiftEvent,
						eventTime: convertIOSDateToHTMLFormat(eventTime || "", DATE_FORMATS.REPORT_DD_MM_YYYY_HH_mm),
						employee: shift.employee,
						date: convertIOSDateToHTMLFormat(createdAt || "", DATE_FORMATS.REPORT_DD_MM_YYYY),
						latLng: geo?.coordinates.length
							? `${geo?.coordinates[1].toFixed(4) || ""} , ${geo?.coordinates[0].toFixed(4) || ""}`
							: "",
						location: shift?.location || "",
						status,
					})
				}
			})
		return data.sort(sortByProperty("eventTime"))
	}, [shift, scheduleId])

	useEffect(() => {
		if (userHasPermissions && !activeRadio) {
			const filteredItems = Object.keys(ScheduleInfoTabs).filter(item => {
				if (item === ScheduleInfoTabs.Employee) {
					return userHasPermissions(Permissions.VIEW_EMPLOYEE_DETAILS)
				}
				return true
			})
			setRadioItems(filteredItems)
			setActiveRadio(filteredItems[0])
		}
	}, [userHasPermissions])

	if (!employeeId && !site?._id) return <></>
	return (
		<div className="flex flex-col space-y-4">
			<Radio.Group
				className="w-full"
				buttonStyle="outline"
				optionType="button"
				value={activeRadio}
				onChange={e => setActiveRadio(e.target.value)}
			>
				<div className="flex w-full">
					{radioItems.map(item => (
						<Radio
							key={item}
							className="flex-1 text-center"
							disabled={item === ScheduleInfoTabs.Employee && !employeeId}
							value={item}
						>
							{item}
						</Radio>
					))}
				</div>
			</Radio.Group>
			<div id="schedule-info-body" className="overflow-y-auto">
				{activeRadio === ScheduleInfoTabs.Employee && <EmployeeInfo id={employeeId} />}
				{activeRadio === ScheduleInfoTabs.Site && <LocationInfo id={site._id ?? ""} />}
				{activeRadio === ScheduleInfoTabs.Activity && (
					<Card className="min-h-80" size="small">
						<div className="flex min-h-80 flex-col">
							{!shift?.incidents?.length && !shift?.shiftEvents?.length && (
								<div className="min-h-full items-center text-center text-lg font-semibold">No Data</div>
							)}
							{!!shift?.incidents?.length && (
								<IncidentView incidents={shift?.incidents ?? []} resolve onResolveCb={onRefetchShift} />
							)}
							<Divider style={{ margin: "8px", marginLeft: "0px", marginRight: "0px" }} />
							{!!(shift?.shiftEvents?.filter(events => events.status !== ShiftStatus.CHASED) || [])
								.length && (
								<div className="flex flex-1 flex-col">
									<div className="flex flex-wrap items-center gap-2">
										<CSVLink
											data={shiftEventsData}
											headers={headers}
											filename={`Report-${shift?.employee}-${shift?.post}`}
										>
											<AntdButton
												className="flex items-center justify-center"
												icon={<CloudDownloadOutlined />}
											>
												CSV
											</AntdButton>
										</CSVLink>
										<AntdButton
											icon={<FilePdfOutlined />}
											className="flex items-center justify-center"
											onClick={() => {
												setIsShiftActivityViewModalOpen(true)
											}}
										>
											Activity
										</AntdButton>
										<AntdButton
											className="flex items-center justify-center"
											icon={<FilePdfOutlined />}
											onClick={() => setOpenSummaryModal(true)}
										>
											Summary
										</AntdButton>
									</div>

									<WorkMonitorView
										shift={shift}
										showVisualizeActivity={true}
										containerClass="flex flex-col flex-1 mt-2 border-none"
									/>
								</div>
							)}
						</div>
					</Card>
				)}
				<ShiftEventPrint
					data={shiftEventsData}
					time={{ startTime: shift?.startTime, finishTime: shift?.finishTime }}
				/>
				<ActivityReportModal
					open={isShiftActivityViewModalOpen}
					onClose={() => setIsShiftActivityViewModalOpen(false)}
				/>
				<ActivitySummaryModal open={openSummaryModal} onClose={() => setOpenSummaryModal(false)} />
			</div>
		</div>
	)
}

interface ShiftEventProps {
	data: ReportShiftInterface[]
	time: { startTime?: string; finishTime?: string }
}

const ShiftEventPrint: React.FC<ShiftEventProps> = ({ data, time }) => {
	const Headers = {
		status: "Status",
		latLng: "Coordinates",
		eventTime: "Event Time",
		dueTime: "Due Time",
		name: "QR Tag",
	}
	return (
		<>
			<div className="grid grid-cols-8">
				<div id="title" className="col-span-2 hidden text-left">
					{data[1]?.employee}
					<br />
					{moment(time.startTime).format(DATE_FORMATS.REPORT_DD_MM_YYYY_HH_mm) +
						" to " +
						moment(time.finishTime).format(DATE_FORMATS.HH_mm)}
				</div>

				<div id="reportName" className="col-span-4 hidden text-center text-md">
					Shift Event Report for {data[1]?.location}
				</div>

				<img
					id="logo"
					src={logo}
					alt="TimeTrail-Login"
					className="col-span-2 hidden h-20  w-auto object-cover"
				/>
			</div>
			<div id="parent" className="hidden  w-full flex-col justify-center">
				{[Headers, ...data]
					.filter(event => event.status !== "CHASED")
					.sort((a, b) => new Date(a.dueTime).valueOf() - new Date(b.dueTime).valueOf())
					.map((entity, index) => {
						return (
							<div
								id="row"
								key={index}
								className="grid grid-cols-12 space-x-2 border-b border-b-gray-300 py-0.25"
							>
								<div className="col-span-1">{entity.status}</div>
								<div className="col-span-3">{entity.latLng ?? "N/A"}</div>
								<div className="col-span-3">{entity.eventTime}</div>
								<div className="col-span-2">
									{moment(entity.dueTime).isValid()
										? moment(entity.dueTime).format("HH:mm")
										: entity.dueTime}
								</div>
								<div className="col-span-3">{entity.name ?? "N/A"}</div>
							</div>
						)
					})}
			</div>
		</>
	)
}

export default ScheduleInfo
